import React, { useEffect } from "react";
import { useRouter } from "next/router";
import { I18nProvider } from "@lingui/react";

import { RootProviders } from "../providers/root";
import { i18n } from "@lingui/core";
import "../components/turntable.css";
import { RequestProvider } from "../contexts/request";
import { useLinguiInit } from "../i18n";
import { getDomain } from 'tldts';
import { datadogLogs } from "@datadog/browser-logs";
import { useLocalStorage } from "react-use";

const App = ({ Component, pageProps }) => {
  const { customOverlay, translation } = pageProps;
  useLinguiInit(translation);

  const router = useRouter();

  const [userInfo, _ ] = useLocalStorage('saweria-user-info', {});

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (datadogLogs.getLogger("default")) return; 

    datadogLogs.init({
      clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
      site: "datadoghq.com", 
      service: "saweria-frontend",
      env: process.env.NEXT_PUBLIC_APP_ENV || "development",
      version: process.env.NEXT_PUBLIC_APP_VERSION || "local",
      forwardErrorsToLogs: true, 
      sampleRate: 100, 
      beforeSend: (log) => {
        const unnecessaryErrors = [
          'encountered two children with the same key', 'network error', 'script error', 'unknown', 'client-side exception', 'method not found'
        ];
        
        if (unnecessaryErrors.some((msg) => log.message?.toLowerCase().includes(msg))) {
          return false;
        }

        return true;
      },
    });
  }, []);

  useEffect(() => {
    if (!userInfo?.id) return;

    try {
      datadogLogs.setUser({
        id: userInfo.id,   
        name: userInfo.username,
      });
    } catch (error) {
      console.warn('Datadog user setup failed:', error);
    }
  }, [userInfo]);

  useEffect(() => {
    if (!router.isReady) return;
  
    const hostname = window.location.hostname;
    const domain = getDomain(hostname, { validHosts: ['localhost'] });
    const { locale, pathname, query, asPath } = router;
  
    // Check if dynamic route params are loaded
    const hasDynamicParams = pathname.includes('[') && Object.keys(query).length === 0;
    if (hasDynamicParams) return; // Wait until query params are populated
  
    const redirectToLocale = (targetLocale) => {
      // For non-dynamic routes or when query params are ready
      if (!pathname.includes('[') || Object.keys(query).length > 0) {
        router.replace({ pathname, query }, asPath, { locale: targetLocale });
      } else {
        // Fallback for dynamic routes before query is ready
        const cleanPath = asPath.split('?')[0].replace(`/${locale}`, '');
        window.location.href = `/${targetLocale}${cleanPath}`;
      }
    };

    const isMax = hostname === 'm-a-x.com' || hostname === 'www.m-a-x.com';
    const isStgMax = hostname.endsWith('.m-a-x.com') && !isMax || userInfo?.currency === 'JPY';
  
    if (domain === 'saweria.co') {
      if (locale !== 'id' && locale !== 'en') {
        redirectToLocale('id');
      }
    } else if (isMax || isStgMax) {
      if (locale !== 'ja') {
        redirectToLocale('ja');
      }
    }
  }, [router, router.isReady, router.query, router.asPath]);

  if (customOverlay) {
    return (
      <RequestProvider>
        <Component {...pageProps} />
      </RequestProvider>
    );
  }

  return (
    <I18nProvider i18n={i18n}>
      <RootProviders>
        <Component {...pageProps} />
      </RootProviders>
    </I18nProvider>
  );
};

export default App;
